import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Sparkles, Upload, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const CustomPluginRequest = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website: '',
    pluginIdea: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to leads table
      const { error } = await supabase.from('leads').insert({
        email: formData.email,
        phone: formData.website,
        onboarding_answers: {
          name: formData.name,
          website: formData.website,
          pluginIdea: formData.pluginIdea,
          type: 'wordpress-plugin-request'
        },
        step_reached: 10
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: 'Request Submitted!',
        description: 'We\'ll review your plugin idea and get back to you soon.',
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: 'Submission Failed',
        description: 'Please try again or contact us directly.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="custom-request" className="py-20 bg-luxury-900">
        <div className="container mx-auto px-6">
          <Card className="max-w-2xl mx-auto bg-luxury-800 border-luxury-700 p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h3 className="text-2xl font-bold mb-4 text-white">
              Thank You!
            </h3>
            <p className="text-white/70 mb-6">
              We've received your custom plugin request. Our team will review it and reach out to you within 24-48 hours with next steps.
            </p>
            <Button onClick={() => setIsSubmitted(false)} variant="outline">
              Submit Another Request
            </Button>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="custom-request" className="py-20 bg-luxury-900">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Custom Development</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
              Need a Custom Plugin?
            </h2>
            <p className="text-lg text-white/70">
              Tell us about your vision and we'll build an AI-powered WordPress plugin tailored to your specific needs.
            </p>
          </div>

          <Card className="bg-luxury-800 border-luxury-700 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-white">Name *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-luxury-900 border-luxury-700 text-white"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-white">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-luxury-900 border-luxury-700 text-white"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="website" className="text-white">Website URL</Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="bg-luxury-900 border-luxury-700 text-white"
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div>
                <Label htmlFor="pluginIdea" className="text-white">Plugin Idea / Goal *</Label>
                <Textarea
                  id="pluginIdea"
                  required
                  value={formData.pluginIdea}
                  onChange={(e) => setFormData({ ...formData, pluginIdea: e.target.value })}
                  className="bg-luxury-900 border-luxury-700 text-white min-h-[150px]"
                  placeholder="Describe what you want your AI-powered plugin to do..."
                />
              </div>

              <div>
                <Label className="text-white">Optional File Upload</Label>
                <div className="mt-2 border-2 border-dashed border-luxury-700 rounded-lg p-8 text-center hover:border-gold/50 transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 text-white/50 mx-auto mb-2" />
                  <p className="text-white/70 text-sm">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-white/50 text-xs mt-1">
                    PDF, DOC, or images (max 10MB)
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
                <Sparkles className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CustomPluginRequest;
